import { useState, useCallback } from 'react'

const HISTORY_KEY = 'sm_api_client_history'
const HISTORY_MAX = 50

export interface ApiClientRequest {
  method: string
  url: string
  headers: Array<{ key: string; value: string }>
  body: string
}

export interface ApiClientResponse {
  status: number
  statusText: string
  headers: Record<string, string>
  body: string
  latencyMs: number
}

export interface HistoryEntry {
  id: string
  timestamp: string
  request: ApiClientRequest
  response: ApiClientResponse
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    return raw ? (JSON.parse(raw) as HistoryEntry[]) : []
  } catch {
    return []
  }
}

function saveHistory(entries: HistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)))
  } catch {
    // localStorage がいっぱいでも握りつぶす
  }
}

export function useApiClient() {
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState<ApiClientResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory())

  const send = useCallback(async (req: ApiClientRequest) => {
    setIsLoading(true)
    setError(null)
    setResponse(null)

    const headers: Record<string, string> = {}
    for (const { key, value } of req.headers) {
      const k = key.trim()
      const v = value.trim()
      if (k) headers[k] = v
    }

    const hasBody = req.body.trim() !== '' && !['GET', 'HEAD'].includes(req.method)
    if (hasBody && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json'
    }

    const start = performance.now()
    try {
      const res = await fetch(req.url, {
        method: req.method,
        headers,
        body: hasBody ? req.body : undefined,
      })

      const latencyMs = Math.round(performance.now() - start)
      const resHeaders: Record<string, string> = {}
      res.headers.forEach((value, key) => { resHeaders[key] = value })
      const resBody = await res.text()

      const result: ApiClientResponse = {
        status: res.status,
        statusText: res.statusText,
        headers: resHeaders,
        body: resBody,
        latencyMs,
      }

      setResponse(result)

      const entry: HistoryEntry = {
        id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        request: req,
        response: result,
      }
      setHistory(prev => {
        const next = [entry, ...prev].slice(0, HISTORY_MAX)
        saveHistory(next)
        return next
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'リクエストに失敗しました'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearHistory = useCallback(() => {
    setHistory([])
    localStorage.removeItem(HISTORY_KEY)
  }, [])

  return { send, isLoading, response, error, history, clearHistory }
}
